import { captureError } from './utils/logger';
import { findBestElement } from './findBestElement';
import { getSelector } from './getSelector';
/**
 * Sanitize captured HTML so that when we later render it inside a sandboxed iframe
 * we never include executable script content or dangerous attributes/URLs.
 * NOTE: Keep this function FAST – it's called in performance-sensitive CLS capture.
 */
export function sanitizeHTML(raw: string): string {
    if (!raw) return '';
    try {
        return raw
            // Remove script / noscript / style tags completely (style can leak external imports)
            .replace(/<\/(?:script|noscript|style)>/gi, '</removed>') // temporary close tag marker to avoid nested confusion
            .replace(/<(script|noscript|style)([\s\S]*?)>([\s\S]*?)<\/removed>/gi, '')
            // Fallback simple removal if above misses
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '')
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            // Strip event handler attributes (on*)
            .replace(/\s+on[a-zA-Z]+="[^"]*"/g, '')
            .replace(/\s+on[a-zA-Z]+='[^']*'/g, '')
            // Strip javascript: and data: urls (except images for context). Keep only http(s) and relative.
            .replace(/(href|src)=("|')(javascript:|data:)([^"']*)("|')/gi, '$1="#"')
            // Neutralize inline styles with url()
            .replace(/style=("|')(.*?)("|')/gi, (m, q1, content, q2) => {
                if (/url\(/i.test(content)) {
                    return 'style=' + q1 + content.replace(/url\([^)]*\)/gi, '') + q2;
                }
                return m;
            })
            // Remove iframes entirely (they cause sandbox errors without allow-scripts)
            .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '<div data-iframe-placeholder="true"></div>')
            // Remove link tags (potential external fetches)
            .replace(/<link[^>]*>/gi, '')
            // Remove meta tags
            .replace(/<meta[^>]*>/gi, '')
            // Collapse multiple spaces/newlines
            .replace(/\s{3,}/g, ' ');
    } catch (error) {
        captureError(error, 'Error sanitizing HTML');
        return '';
    }
}


// Collect detailed element data including styles and HTML structure
export interface PrecomputedRectLike {
    x: number; y: number; width: number; height: number;
    top: number; left: number; right: number; bottom: number;
}

export function collectElementData(element: Element, precomputedRect?: PrecomputedRectLike): any {
    if (!element) return null;

    let highlightedElement = false;

    try {
        const perfStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
        let perfHierarchyStart = 0;
        let perfHierarchyEnd = 0;
        let perfStylesCount = 0;


        // Find the best element to collect data from
        const targetElement = findBestElement(element);


        // Get computed styles for an element
        const getElementStyles = (element: Element) => {
            const computed = getComputedStyle(element);

            // Try direct property access instead of getPropertyValue
            const safeGetProperty = (prop: string) => {
                try {
                    // Convert kebab-case to camelCase for direct property access
                    const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                    const value = (computed as any)[camelProp] || computed.getPropertyValue(prop) || '';
                    return value;
                } catch (error) {
                    captureError(error, `Error getting computed value ${prop}`);
                    return '';
                }
            };

            const styles = {
                // Layout properties
                display: safeGetProperty('display'),
                position: safeGetProperty('position'),
                top: safeGetProperty('top'),
                left: safeGetProperty('left'),
                right: safeGetProperty('right'),
                bottom: safeGetProperty('bottom'),
                width: safeGetProperty('width'),
                height: safeGetProperty('height'),
                minWidth: safeGetProperty('min-width'),
                minHeight: safeGetProperty('min-height'),
                maxWidth: safeGetProperty('max-width'),
                maxHeight: safeGetProperty('max-height'),

                // Box model
                margin: safeGetProperty('margin'),
                marginTop: safeGetProperty('margin-top'),
                marginRight: safeGetProperty('margin-right'),
                marginBottom: safeGetProperty('margin-bottom'),
                marginLeft: safeGetProperty('margin-left'),
                padding: safeGetProperty('padding'),
                paddingTop: safeGetProperty('padding-top'),
                paddingRight: safeGetProperty('padding-right'),
                paddingBottom: safeGetProperty('padding-bottom'),
                paddingLeft: safeGetProperty('padding-left'),

                // Border
                border: safeGetProperty('border'),
                borderWidth: safeGetProperty('border-width'),
                borderStyle: safeGetProperty('border-style'),
                borderColor: safeGetProperty('border-color'),
                borderRadius: safeGetProperty('border-radius'),

                // Background
                background: safeGetProperty('background'),
                backgroundColor: safeGetProperty('background-color'),
                backgroundImage: safeGetProperty('background-image'),
                backgroundSize: safeGetProperty('background-size'),
                backgroundPosition: safeGetProperty('background-position'),
                backgroundRepeat: safeGetProperty('background-repeat'),

                // Typography
                color: safeGetProperty('color'),
                fontSize: safeGetProperty('font-size'),
                fontFamily: safeGetProperty('font-family'),
                fontWeight: safeGetProperty('font-weight'),
                lineHeight: safeGetProperty('line-height'),
                textAlign: safeGetProperty('text-align'),
                textDecoration: safeGetProperty('text-decoration'),

                // Flex & Grid
                flexDirection: safeGetProperty('flex-direction'),
                justifyContent: safeGetProperty('justify-content'),
                alignItems: safeGetProperty('align-items'),
                flex: safeGetProperty('flex'),
                flexGrow: safeGetProperty('flex-grow'),
                flexShrink: safeGetProperty('flex-shrink'),

                // Other
                zIndex: safeGetProperty('z-index'),
                opacity: safeGetProperty('opacity'),
                transform: safeGetProperty('transform'),
                transition: safeGetProperty('transition'),
                overflow: safeGetProperty('overflow'),
                visibility: safeGetProperty('visibility')
            };

            return styles;
        };

        // Convert computed styles to inline style string
        const computedStylesToInline = (styles: any): string => {
            const styleProps = [];

            // ALWAYS include ALL properties that have any meaningful value
            Object.entries(styles).forEach(([prop, value]) => {
                if (!value || value === '' || value === 'initial' || value === 'inherit') return;

                // Convert camelCase to kebab-case
                const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();

                // Skip obviously default values but be more lenient
                const skipValues = ['auto', 'none', 'normal', '0px', 'rgba(0, 0, 0, 0)', 'transparent', 'static', 'visible', 'start'];
                if (skipValues.includes(String(value))) {
                    // But always include critical layout properties even if "auto"
                    const criticalProps = ['position', 'display', 'width', 'height', 'color', 'font-size', 'font-family'];
                    if (!criticalProps.includes(prop)) return;
                }

                styleProps.push(`${cssProp}: ${value}`);
            });

            // If we still have no properties, force some basic ones
            if (styleProps.length === 0) {
                if (styles.display) styleProps.push(`display: ${styles.display}`);
                if (styles.position) styleProps.push(`position: ${styles.position}`);
                if (styles.color) styleProps.push(`color: ${styles.color}`);
                if (styles.backgroundColor) styleProps.push(`background-color: ${styles.backgroundColor}`);
                if (styles.fontSize) styleProps.push(`font-size: ${styles.fontSize}`);
                if (styles.fontFamily) styleProps.push(`font-family: ${styles.fontFamily}`);
            }

            const result = styleProps.join('; ');
            return result;
        };

        const getElementHierarchy = (el: Element, highlightElement: Element): string => {
            perfHierarchyStart = typeof performance !== 'undefined' ? performance.now() : Date.now();

            // Apply inline styles by reading from the ORIGINAL DOM and writing to the CLONED tree
            const applyInlineStylesFromOriginal = (originalEl: Element, clonedEl: Element): void => {
                if (!originalEl || !clonedEl) return;

                // Mark the highlight element with a data attribute for easy selection
                if (originalEl === highlightElement) {
                    try {
                        clonedEl.setAttribute('data-cls-target-element', 'true');
                        highlightedElement = true;
                    } catch (err) {
                        captureError(err, 'Failed to mark target element');
                    }
                }

                // Skip non-visual tags
                const tag = originalEl.tagName?.toLowerCase() || '';
                if (tag === 'script' || tag === 'style' || tag === 'link' || tag === 'meta') {
                    return;
                }

                // Replace iframes with a placeholder preserving size (avoid including iframe itself)
                if (tag === 'iframe') {
                    try {
                        const rect = originalEl.getBoundingClientRect();
                        const placeholder = document.createElement('div');
                        const w = Math.max(Math.round(rect.width), 50);
                        const h = Math.max(Math.round(rect.height), 30);
                        placeholder.textContent = 'iframe';
                        placeholder.setAttribute('data-iframe-placeholder', 'true');
                        placeholder.className = 'cls-iframe-placeholder';
                        // Basic neutral styling so reconstructed view shows occupied space
                        placeholder.setAttribute('style', [
                            `display: block`,
                            `width: ${w}px`,
                            `height: ${h}px`,
                            `background: repeating-linear-gradient(45deg,#f2f2f2,#f2f2f2 10px,#e5e5e5 10px,#e5e5e5 20px)`,
                            `border: 1px dashed #999`,
                            `box-sizing: border-box`,
                            `color: #555`,
                            `font: 11px/1.4 monospace`,
                            `display: flex`,
                            `align-items: center`,
                            `justify-content: center`,
                            `text-transform: uppercase`,
                            `letter-spacing: 1px`
                        ].join('; '));
                        const parent = clonedEl.parentElement;
                        if (parent) parent.replaceChild(placeholder, clonedEl);
                    } catch (err) {
                        captureError(err, 'CLS: failed to create iframe placeholder');
                    }
                    return; // Do not recurse into iframe
                }

                // Compute styles from the original element attached to document
                const styles = getElementStyles(originalEl);
                const inlineStyles = computedStylesToInline(styles);
                if (inlineStyles) perfStylesCount += 1;

                if (inlineStyles && inlineStyles.length > 0) {
                    try {
                        clonedEl.setAttribute('style', inlineStyles);
                    } catch (error) {
                        captureError(error, '❌ Could not set inline styles on cloned element');
                    }
                } else {
                    // Removed debug logging for no inline styles
                }

                // Recurse over children in index order (elements only)
                const origChildren = Array.from(originalEl.children);
                const cloneChildren = Array.from(clonedEl.children);
                const count = Math.min(origChildren.length, cloneChildren.length);
                for (let i = 0; i < count; i++) {
                    applyInlineStylesFromOriginal(origChildren[i], cloneChildren[i]);
                }
            };

            // Clone the top element to avoid modifying the original DOM
            const clonedElement = el.cloneNode(true) as Element;

            // Apply inline styles to the cloned hierarchy using the original as source
            // The highlightElement will be marked during recursion
            applyInlineStylesFromOriginal(el, clonedElement);

            // Get the outerHTML with all inline styles applied, then sanitize to strip any scripts/unsafe attrs
            const rawHierarchyHTML = clonedElement.outerHTML;
            const hierarchyHTML = sanitizeHTML(rawHierarchyHTML);
            perfHierarchyEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();
            return hierarchyHTML;
        };

        const elementSelector = getSelector(element);
        const result = {
            // Use simplified hierarchy as HTML string with inline styles
            // Pass both: targetElement for hierarchy root, element for highlighting
            hierarchyHTML: getElementHierarchy(targetElement, element),
            // Keep basic element info for compatibility
            elementInfo: {
                selector: elementSelector,
                tagName: element.tagName?.toLowerCase() || null,
                id: element.id || null,
                className: element.className || null
            },
            // Add target element info
            targetElementInfo: {
                selector: getSelector(targetElement),
                className: targetElement.className || '',
                id: targetElement.id || '',
                tagName: targetElement.tagName?.toLowerCase() || 'div',
            }
        };


        if (!highlightedElement) {
            captureError(new Error('Highlight element not found in hierarchy'), elementSelector);
        }
        const perfEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const totalMs = (perfEnd - perfStart).toFixed(2);
        const hierarchyMs = perfHierarchyEnd ? (perfHierarchyEnd - perfHierarchyStart).toFixed(2) : '0';
        return result;

    } catch (error) {
        captureError(error, 'Error collecting element data');
        return null;
    }
}